import React from 'react';
import cn from 'classnames';
import { ResponsivePie } from '@nivo/pie';

import { formatDate } from 'helpers/formatDate';
import { getTranslation } from 'helpers/getTranslation';

import { Typography } from 'components/shared/Typography';
import { CuratorReportAccessFilter } from './components/CuratorReportAccessFilter';
import { CuratorReportLoading } from './components/CuratorReportLoading';
import { CuratorReportProgressChart } from './components/CuratorReportProgressChart';
import { Button } from 'components/shared/Button';
import { Icon } from 'components/shared/Icon';
import { Table } from 'components/shared/Table';
import { Avatar } from 'components/shared/Avatar';

import { EmptyState } from 'components/EmptyState';
import { SchoolPreview } from 'components/SchoolPreview';
import { MenuLayout } from 'components/Layout';
import { TablePagination } from 'components/TablePagination';
import { TableToolbar } from 'components/TableToolbar';

import {
  formatDaysInactive,
  formatProgressPercent,
  formatRatingScore,
  formatStudentAccessUntil,
  isAllLessonsCompleted,
  getStallPointRatingColor,
  getStatusBadgeClassName,
  getStudentAvatarText,
  getStudentDisplayName,
  getStudentStatusLabel,
  getSummaryCards,
  hasActiveAccessFilter,
  hasStudentName,
  type CuratorReportCurrentLesson,
  type CuratorReportData,
  type CuratorReportPagination,
  type CuratorReportSortParams,
  type CuratorReportStudent,
  type CuratorReportStudentStatus,
} from './SchoolCuratorReportPage.helpers';
import { schoolCuratorReportPerPageOptions } from './SchoolCuratorReportPage.pagination.config';
import {
  getCourseProgressDonutData,
  getCourseProgressDonutPadAngle,
} from '../SchoolCuratorStudentPage/SchoolCuratorStudentPage.helpers';

import styles from './SchoolCuratorReportPage.module.scss';

const PROGRESS_COLUMN_MIN_WIDTH = 90;
const CURRENT_LESSON_COLUMN_MIN_WIDTH = 300;
const TABLE_PROGRESS_DONUT_COLOR = '#1182f6';
const TABLE_PROGRESS_DONUT_MUTED_COLOR = '#889ab1';

const getCurrentLessonPreviewUrl = (
  currentLesson: CuratorReportCurrentLesson,
) =>
  currentLesson.course.preview ?? currentLesson.module?.preview ?? null;

const CurrentLessonCell = ({
  currentLesson,
  progressPercent,
  status,
}: {
  currentLesson: CuratorReportCurrentLesson | null;
  progressPercent: number;
  status: CuratorReportStudentStatus;
}) => {
  if (isAllLessonsCompleted(currentLesson, progressPercent)) {
    return (
      <div className={styles.currentLessonCell}>
        <span className={styles.currentLessonCompletedIconWrap}>
          <Icon
            name="checkRound"
            className={styles.currentLessonCompletedIcon}
          />
        </span>
        <Typography
          className={styles.currentLessonCompletedText}
          variant="body3"
          weight="medium"
          mode="compact"
        >
          {getTranslation('school_curator_report_page.all_lessons_completed')}
        </Typography>
      </div>
    );
  }

  if (status === 'access_expired') {
    return (
      <div className={styles.currentLessonCell}>
        <TableProgressDonut
          progress={progressPercent}
          color={TABLE_PROGRESS_DONUT_MUTED_COLOR}
        />
        <Typography
          className={styles.accessExpiredProgressText}
          variant="body3"
          weight="medium"
          mode="compact"
        >
          {getTranslation(
            'school_curator_report_page.access_expired_progress',
            { percent: Math.round(progressPercent) },
          )}
        </Typography>
      </div>
    );
  }

  if (!currentLesson) {
    return (
      <Typography variant="body3" mode="compact">
        {getTranslation('school_curator_report_page.not_available')}
      </Typography>
    );
  }

  const previewUrl = getCurrentLessonPreviewUrl(currentLesson);

  return (
    <div className={styles.currentLessonCell}>
      {previewUrl ? (
        <img
          className={styles.currentLessonPreview}
          src={previewUrl}
          alt=""
        />
      ) : (
        <div className={styles.currentLessonPreviewPlaceholder} />
      )}
      <div className={styles.currentLessonInfo}>
        <Typography
          className={styles.currentLessonCourse}
          variant="caption"
          mode="compact"
        >
          {currentLesson.course.title}
        </Typography>
        {currentLesson.module ? (
          <Typography
            className={styles.currentLessonModule}
            variant="caption"
            mode="compact"
          >
            {getTranslation(
              'school_curator_report_page.current_lesson_module',
              {
                title: currentLesson.module.title,
              },
            )}
          </Typography>
        ) : null}
        <Typography
          className={styles.currentLessonTitle}
          variant="body3"
          mode="compact"
        >
          {currentLesson.title}
        </Typography>
      </div>
    </div>
  );
};

const TableProgressDonut = ({
  progress,
  color = TABLE_PROGRESS_DONUT_COLOR,
}: {
  progress: number;
  color?: string;
}) => (
  <div className={styles.tableProgressDonut}>
    <ResponsivePie
      colors={{ datum: 'data.color' }}
      data={getCourseProgressDonutData(progress, color)}
      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
      innerRadius={0.72}
      padAngle={getCourseProgressDonutPadAngle(progress)}
      cornerRadius={2}
      activeOuterRadiusOffset={4}
      enableArcLabels={false}
      enableArcLinkLabels={false}
      isInteractive={false}
      layers={[
        'arcs',
        (props: { centerX: number; centerY: number }) => (
          <text
            x={props.centerX}
            y={props.centerY}
            textAnchor="middle"
            dominantBaseline="central"
            className={styles.tableProgressDonutCenterMetric}
          >
            {formatProgressPercent(progress)}
          </text>
        ),
      ]}
    />
  </div>
);

const SUMMARY_CARD_VARIANT_CLASS: Record<string, string> = {
  default: styles.summaryCardDefault,
  success: styles.summaryCardSuccess,
  stuck: styles.summaryCardStuck,
  warning: styles.summaryCardWarning,
  info: styles.summaryCardInfo,
  muted: styles.summaryCardMuted,
};

const INDEX_COLUMN_MIN_WIDTH = 48;

const getTableColumns = () => [
  {
    key: 'index',
    label: '#',
    minWidth: INDEX_COLUMN_MIN_WIDTH,
    maxWidth: INDEX_COLUMN_MIN_WIDTH,
    justifyContent: 'center',
  },
  {
    key: 'student',
    label: getTranslation('school_curator_report_page.table.student'),
    minWidth: 280,
  },
  {
    key: 'progress',
    label: getTranslation('school_curator_report_page.table.progress'),
    minWidth: PROGRESS_COLUMN_MIN_WIDTH,
    sortable: true,
  },
  {
    key: 'status',
    label: getTranslation('school_curator_report_page.table.status'),
    minWidth: 120,
    sortable: true,
  },
  {
    key: 'currentLesson',
    label: getTranslation('school_curator_report_page.table.current_lesson'),
    minWidth: CURRENT_LESSON_COLUMN_MIN_WIDTH,
  },
  {
    key: 'daysInactive',
    label: getTranslation('school_curator_report_page.table.days_inactive'),
    minWidth: 110,
    sortable: true,
  },
  {
    key: 'access',
    label: getTranslation('school_curator_report_page.table.access'),
    minWidth: 140,
    sortable: true,
  },
];

const renderTableCell = (
  {
    key,
    item,
    itemIndex,
    className,
    locale,
    currentPage,
    perPage,
  }: {
    key: string;
    item: any;
    itemIndex: number;
    className: string;
    locale: string;
    currentPage: number;
    perPage: number;
  },
  Cell: React.ComponentType<any>,
) => {
  switch (key) {
    case 'index':
      return (
        <Cell
          key={key}
          className={className}
          minWidth={INDEX_COLUMN_MIN_WIDTH}
          maxWidth={INDEX_COLUMN_MIN_WIDTH}
          justifyContent="center"
        >
          <Typography
            className={styles.indexCell}
            variant="body3"
            mode="compact"
          >
            {(currentPage - 1) * perPage + itemIndex + 1}
          </Typography>
        </Cell>
      );
    case 'student':
      return (
        <Cell key={key} className={className} minWidth={280}>
          <div className={styles.studentCell}>
            <Avatar
              className={styles.studentAvatar}
              size="sm"
              url={item.avatar}
              text={getStudentAvatarText(item)}
            />
            <div className={styles.studentInfo}>
              <Typography
                className={cn(styles.studentName, {
                  [styles.studentNameUnset]: !hasStudentName(item.name),
                })}
                variant="body3"
                weight="medium"
                mode="compact"
              >
                {getStudentDisplayName(item.name)}
              </Typography>
              <Typography
                className={styles.studentEmail}
                variant="caption"
                mode="compact"
              >
                {item.email || '—'}
              </Typography>
            </div>
          </div>
        </Cell>
      );
    case 'progress':
      return (
        <Cell key={key} className={className} minWidth={PROGRESS_COLUMN_MIN_WIDTH}>
          <div className={styles.progressCell}>
            <TableProgressDonut progress={item.progressPercent} />
          </div>
        </Cell>
      );
    case 'status':
      return (
        <Cell key={key} className={className} minWidth={120}>
          <div
            className={cn(
              styles.badge,
              styles[getStatusBadgeClassName(item.status)],
            )}
          >
            <Typography
              className={styles.badgeText}
              variant="caption"
              weight="medium"
              mode="compact"
            >
              {getStudentStatusLabel(item.status)}
            </Typography>
          </div>
        </Cell>
      );
    case 'currentLesson':
      return (
        <Cell
          key={key}
          className={className}
          minWidth={CURRENT_LESSON_COLUMN_MIN_WIDTH}
        >
          <CurrentLessonCell
            currentLesson={item.currentLesson}
            progressPercent={item.progressPercent}
            status={item.status}
          />
        </Cell>
      );
    case 'daysInactive':
      return (
        <Cell key={key} className={className} minWidth={110}>
          <Typography variant="body3" mode="compact">
            {formatDaysInactive(item.daysInactive)}
          </Typography>
        </Cell>
      );
    case 'access':
      return (
        <Cell key={key} className={className} minWidth={140}>
          <Typography
            className={cn(styles.accessCellText, {
              [styles.accessCellTextExpired]: item.status === 'access_expired',
            })}
            variant="body3"
            mode="compact"
          >
            {formatStudentAccessUntil(item.promocodeAccess, locale)}
          </Typography>
        </Cell>
      );
    default:
      return null;
  }
};

const SchoolCuratorReportPage = ({
  locale,
  isPublic,
  loading,
  reportData,
  students,
  pagination,
  searchQuery,
  accessFrom,
  accessTo,
  onAccessFromChange,
  onAccessToChange,
  currentPage,
  perPage,
  onSearchChange,
  onPerPageChange,
  onNextPageClick,
  onPrevPageClick,
  onCopyShareLink,
  isCopyingShareLink,
  onRefreshReport,
  isRefreshing,
  onStudentClick,
  sortParams,
  onSortChange,
}: {
  locale: string;
  isPublic: boolean;
  loading: boolean;
  reportData: CuratorReportData | null;
  students: CuratorReportStudent[];
  pagination?: CuratorReportPagination;
  searchQuery: string;
  accessFrom: string;
  accessTo: string;
  onAccessFromChange: (value: string) => void;
  onAccessToChange: (value: string) => void;
  currentPage: number;
  perPage: number;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPerPageChange: (nextPerPage: number) => void;
  onNextPageClick: () => void;
  onPrevPageClick: () => void;
  onCopyShareLink?: () => void;
  isCopyingShareLink?: boolean;
  onRefreshReport?: () => void;
  isRefreshing?: boolean;
  onStudentClick?: (studentId: string, primaryCourseId: string) => void;
  sortParams: CuratorReportSortParams;
  onSortChange: (sortKey: string) => void;
}) => {
  const totalPages = pagination?.totalPages ?? 1;
  const hasStudents = Boolean(reportData?.summary.total);
  const hasFilteredStudents = (pagination?.total ?? students.length) > 0;
  const hasActiveSearch = Boolean(searchQuery.trim());
  const hasActiveFilters =
    hasActiveSearch || hasActiveAccessFilter(accessFrom, accessTo);
  const isContentUpdating = Boolean(
    reportData && (loading || isRefreshing),
  );
  const sectionLoadingTitleKey = isRefreshing
    ? 'school_curator_report_page.loading.refresh_title'
    : 'school_curator_report_page.loading.table_title';
  const sectionLoadingSubtitleKey = isRefreshing
    ? 'school_curator_report_page.loading.refresh_subtitle'
    : 'school_curator_report_page.loading.table_subtitle';

  const renderStudentsTable = (tableStudents: CuratorReportStudent[]) => (
    <div className={cn(styles.sectionWrapper, styles.tableWrapper)}>
      <Table
        className={styles.table}
        headerClassName={styles.tableHeader}
        rowClassName={styles.tableRow}
        data={tableStudents}
        columns={getTableColumns()}
        params={sortParams}
        onSortChange={onSortChange}
        onRowClick={
          onStudentClick
            ? (item: CuratorReportStudent) =>
                onStudentClick(item.id, item.primaryCourseId)
            : undefined
        }
        cellRenderer={(props: any, Cell: any) =>
          renderTableCell(
            { ...props, locale, currentPage, perPage },
            Cell,
          )
        }
      />
    </div>
  );

  const pageContent = (
    <div
      className={cn(styles.page, {
        [styles.pagePublic]: isPublic,
      })}
    >
      <div
        className={cn(styles.content, {
          [styles.contentProtected]: !isPublic,
        })}
      >
        {loading && !reportData ? (
          <CuratorReportLoading
            titleKey="school_curator_report_page.loading.title"
            subtitleKey="school_curator_report_page.loading.subtitle"
          />
        ) : reportData ? (
          <>
            <div className={styles.header}>
              <div className={styles.headerMain}>
                <SchoolPreview
                  className={styles.schoolPreview}
                  size="lg"
                  preview={reportData.school.preview}
                  name={reportData.school.name}
                />
                <div className={styles.headerInfo}>
                  <Typography
                    className={styles.schoolName}
                    variant="subtitle"
                    weight="medium"
                    mode="compact"
                  >
                    {reportData.school.name}
                  </Typography>
                  <Typography
                    className={styles.updatedAt}
                    variant="caption"
                    mode="compact"
                  >
                    {getTranslation('school_curator_report_page.updated_at', {
                      date: formatDate(
                        reportData.updatedAt,
                        'dd MMM yyyy, HH:mm',
                        locale,
                      ),
                    })}
                  </Typography>
                </div>
              </div>

              <div className={styles.headerActions}>
                {onRefreshReport ? (
                  <Button
                    size="md"
                    variant="secondary"
                    startIcon={
                      <Icon
                        name="resync"
                        className={cn({
                          [styles.refreshButtonIconSpinning]: isRefreshing,
                        })}
                      />
                    }
                    disabled={loading || isRefreshing}
                    onClick={onRefreshReport}
                  >
                    {getTranslation('school_curator_report_page.refresh_report')}
                  </Button>
                ) : null}
                {!isPublic && onCopyShareLink ? (
                  <Button
                    size="md"
                    variant="secondary"
                    startIcon={<Icon name="copy" />}
                    disabled={isCopyingShareLink}
                    onClick={onCopyShareLink}
                  >
                    {getTranslation(
                      'school_curator_report_page.copy_share_link',
                    )}
                  </Button>
                ) : null}
              </div>
            </div>

            <div
              className={cn(styles.reportBody, {
                [styles.reportBodyLoading]: isContentUpdating,
              })}
            >
              <div className={styles.reportBodyContent}>
                <div className={styles.summaryGridSection}>
                  <div className={styles.summaryGrid}>
                    {getSummaryCards(reportData.summary).map((card) => (
                      <div
                        key={card.key}
                        className={cn(
                          styles.summaryCard,
                          SUMMARY_CARD_VARIANT_CLASS[card.variant],
                        )}
                      >
                        <div className={styles.summaryIconBadge}>
                          <Icon
                            name={card.icon}
                            className={styles.summaryIcon}
                          />
                        </div>
                        <div className={styles.summaryContent}>
                          <Typography
                            className={styles.summaryLabel}
                            variant="caption"
                            mode="compact"
                          >
                            {card.label}
                          </Typography>
                          <Typography
                            className={styles.summaryValue}
                            variant="subtitle"
                            weight="medium"
                            mode="compact"
                          >
                            {card.value}
                          </Typography>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {reportData.courseProgressCharts.length ? (
                  <CuratorReportProgressChart
                    courseProgressCharts={reportData.courseProgressCharts}
                  />
                ) : null}

                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <div>
                      <Typography
                        className={styles.sectionTitle}
                        variant="body1"
                        weight="medium"
                        mode="compact"
                      >
                        {getTranslation(
                          'school_curator_report_page.students_title',
                        )}
                      </Typography>
                    </div>
                  </div>

                  {hasStudents ? (
                    <div className={styles.tableToolbar}>
                      <div className={styles.tableToolbarFilters}>
                        <CuratorReportAccessFilter
                          accessFrom={accessFrom}
                          accessTo={accessTo}
                          onAccessFromChange={onAccessFromChange}
                          onAccessToChange={onAccessToChange}
                        />
                      </div>
                      <div className={styles.tableToolbarSearch}>
                        <TableToolbar
                          className={styles.tableToolbarSearchInner}
                          onSearchChange={onSearchChange}
                        />
                      </div>
                    </div>
                  ) : null}

                  {hasFilteredStudents ? (
                    <>
                      {renderStudentsTable(students)}
                      <TablePagination
                        currentPage={currentPage}
                        perPage={perPage}
                        totalPages={totalPages}
                        perPageOptions={schoolCuratorReportPerPageOptions}
                        onPerPageChange={onPerPageChange}
                        onNextPageClick={onNextPageClick}
                        onPrevPageClick={onPrevPageClick}
                      />
                    </>
                  ) : hasActiveFilters && pagination?.total === 0 ? (
                    <EmptyState
                      className={styles.emptySearchResults}
                      icon="search"
                      title={getTranslation(
                        'school_curator_report_page.no_search_results.title',
                      )}
                      text={getTranslation(
                        'school_curator_report_page.no_search_results.text',
                      )}
                    />
                  ) : (
                    renderStudentsTable([])
                  )}
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <div>
                      <Typography
                        className={styles.sectionTitle}
                        variant="body1"
                        weight="medium"
                        mode="compact"
                      >
                        {getTranslation(
                          'school_curator_report_page.stall_points_title',
                        )}
                      </Typography>
                      <Typography
                        className={styles.sectionSubtitle}
                        variant="caption"
                        mode="compact"
                      >
                        {getTranslation(
                          'school_curator_report_page.stall_points_subtitle',
                        )}
                      </Typography>
                    </div>
                  </div>

                  {reportData.stallPoints.length ? (
                    <div className={styles.stallPointsGrid}>
                      {reportData.stallPoints.map((stallPoint) => (
                        <div
                          key={stallPoint.lessonId}
                          className={styles.stallPointCard}
                        >
                          <Typography
                            className={styles.stallPointTitle}
                            variant="body2"
                            weight="medium"
                            mode="compact"
                          >
                            {stallPoint.lessonTitle}
                          </Typography>
                          <div className={styles.stallPointMeta}>
                            <div className={styles.stallPointRating}>
                              <span
                                className={styles.ratingDot}
                                style={{
                                  backgroundColor: getStallPointRatingColor(
                                    stallPoint.averageRating,
                                  ),
                                }}
                              />
                              <Typography variant="body3" mode="compact">
                                {formatRatingScore(stallPoint.averageRating)}
                              </Typography>
                            </div>
                            <Typography
                              className={styles.stallPointReports}
                              variant="caption"
                              mode="compact"
                            >
                              {getTranslation(
                                'school_curator_report_page.reports_count',
                                { count: stallPoint.reportCount },
                              )}
                            </Typography>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      className={styles.emptyStallPoints}
                      icon="checkRound"
                      title={getTranslation(
                        'school_curator_report_page.no_stall_points.title',
                      )}
                      text={getTranslation(
                        'school_curator_report_page.no_stall_points.text',
                      )}
                    />
                  )}
                </div>
              </div>

              {isContentUpdating ? (
                <div className={styles.reportBodyLoadingOverlay}>
                  <CuratorReportLoading
                    variant="section"
                    titleKey={sectionLoadingTitleKey}
                    subtitleKey={sectionLoadingSubtitleKey}
                  />
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <EmptyState
            className={styles.emptyCourseState}
            icon="drafts"
            title={getTranslation('common.toast.error.title')}
            text={getTranslation('common.toast.error.text')}
          />
        )}
      </div>
    </div>
  );

  if (isPublic) {
    return pageContent;
  }

  return <MenuLayout>{pageContent}</MenuLayout>;
};

export default React.memo(SchoolCuratorReportPage) as any;
